// Setzliste: Modus + Anzahl (ITF-Vorschlag, LK-abhängig)

import React, { useMemo } from "react";
import {
  suggestOfficialSeedCount,
  countPlayersWithLk,
  resolveEffectiveSeeding,
  listAllowedSeedCounts,
} from "@tennis-matches/core/utils";

export function ClubSeedingFields({
  t,
  entrantsCount,
  names,
  lkByPlayer,
  seedingMode,
  seedCount,
  seedingTouched,
  onSeedingChange,
}) {
  const rankedCount = useMemo(
    () => countPlayersWithLk(names, lkByPlayer),
    [names, lkByPlayer],
  );
  const suggested = useMemo(
    () => suggestOfficialSeedCount(entrantsCount),
    [entrantsCount],
  );
  const hasLk = rankedCount > 0;

  const displaySeedCount = useMemo(() => {
    if (!hasLk) return 0;
    if ((seedCount ?? 0) > 0) return seedCount;
    if (!seedingTouched) return Math.min(suggested, rankedCount);
    return resolveEffectiveSeeding({
      seedingMode,
      seedCount,
      entrantsCount,
      rankedCount,
    }).seedCount;
  }, [
    hasLk,
    seedCount,
    seedingTouched,
    suggested,
    rankedCount,
    seedingMode,
    entrantsCount,
  ]);

  const displayMode = useMemo(() => {
    if (!hasLk) return "random";
    if (seedingTouched) return seedingMode === "seeded" ? "seeded" : "random";
    if (displaySeedCount > 0) return "seeded";
    return seedingMode === "seeded" ? "seeded" : "random";
  }, [hasLk, seedingTouched, seedingMode, displaySeedCount]);

  const options = useMemo(
    () =>
      listAllowedSeedCounts(entrantsCount, rankedCount).filter((n) => n > 0),
    [entrantsCount, rankedCount],
  );

  const setMode = (mode) => {
    onSeedingChange({
      seedingMode: mode,
      seedCount: mode === "seeded" ? displaySeedCount : 0,
      seedingTouched: true,
    });
  };

  const setCount = (count) => {
    onSeedingChange({
      seedingMode: "seeded",
      seedCount: count,
      seedingTouched: true,
    });
  };

  const applySuggested = () => {
    onSeedingChange({
      seedingMode: "seeded",
      seedCount: Math.min(suggested, rankedCount),
      seedingTouched: true,
    });
  };

  return (
    <div className="wizardStep__subsection" data-section="seeding">
      {!hasLk ? (
        <p className="tm-wizard-hint-box tm-wizard-hint-box--inline">
          {t("club.seeding.noLkHint")}
        </p>
      ) : null}

      <label className="wizardField">
        <span className="wizardField__label">{t("club.ko.seeding")}</span>
        <select
          className="wizardField__input"
          value={displayMode}
          disabled={!hasLk}
          onChange={(e) => setMode(e.target.value)}
        >
          <option value="random">{t("club.groupsko.seeding.random")}</option>
          <option value="seeded" disabled={!hasLk}>
            {t("club.groupsko.seeding.seededLk")}
          </option>
        </select>
      </label>

      {displayMode === "seeded" && hasLk ? (
        <>
          <label className="wizardField">
            <span className="wizardField__label">
              {t("club.seeding.count")}
            </span>
            <select
              className="wizardField__input"
              value={displaySeedCount}
              onChange={(e) => setCount(Number(e.target.value))}
            >
              {options.map((n) => (
                <option key={n} value={n}>
                  {t("club.seeding.countOption", { n })}
                </option>
              ))}
            </select>
          </label>
          <p className="wizardStep__hint">
            {t("club.seeding.suggested", { n: suggested })}
          </p>
          {!seedingTouched ||
          displaySeedCount !== Math.min(suggested, rankedCount) ? (
            <button
              type="button"
              className="tm-wizard-v0__btn"
              onClick={applySuggested}
            >
              {t("club.seeding.applySuggested", {
                n: Math.min(suggested, rankedCount),
              })}
            </button>
          ) : null}
        </>
      ) : null}
    </div>
  );
}

/**
 * Normalisiert Setzliste für Persistenz (inkl. Vorschlag wenn noch nicht manuell gesetzt).
 */
export function resolveClubSeedingForSave({
  seedingMode,
  seedCount,
  seedingTouched,
  entrantsCount,
  names,
  lkByPlayer,
}) {
  const rankedCount = countPlayersWithLk(names, lkByPlayer);
  let mode = seedingMode ?? "random";
  let count = Number(seedCount) || 0;
  if (rankedCount < 1) {
    return { seedingMode: "random", seedCount: 0 };
  }
  if (!seedingTouched && count <= 0) {
    mode = "seeded";
    count = Math.min(suggestOfficialSeedCount(entrantsCount), rankedCount);
  }
  return resolveEffectiveSeeding({
    seedingMode: mode,
    seedCount: count,
    entrantsCount,
    rankedCount,
  });
}
